import {
  useColorModeValue,
  Stack,
  Heading,
  FormControl,
  Input,
  Button,
  FormLabel,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Link,
  Image,
  InputGroup,
  InputRightAddon,
  HStack,
  VStack,
  useToast,
  Spinner,
} from '@chakra-ui/react'
import { parseEther } from 'ethers/lib/utils.js'
import { useState } from 'react'
import {
  useAccount,
  usePrepareSendTransaction,
  useSendTransaction,
  useWaitForTransaction,
} from 'wagmi'
import { useDebounce } from '../hooks'
import TransferSvg from '../assets/transfer.svg'
import { shortAddress } from '../utils/minorUtils'

export default function TransferToken() {
  const [to, setTo] = useState('')
  const debouncedTo = useDebounce(to, 500)
  const { address, isDisconnected } = useAccount()
  const toast = useToast()
  const [amount, setAmount] = useState('')
  const debouncedAmount = useDebounce(amount, 500)

  const { config } = usePrepareSendTransaction({
    request: {
      to: debouncedTo,
      value: debouncedAmount ? parseEther(debouncedAmount) : undefined,
    },
  })
  const { data, sendTransaction } = useSendTransaction(config)

  const { isLoading } = useWaitForTransaction({
    hash: data?.hash,
    onSuccess(data) {
      setTo('')
      setAmount('')
      toast({
        position: 'bottom-right',
        duration: 6000,
        render: () => (
          <Stat bgColor={'brand.50'} py={'3'} px={'8'} borderRadius={'lg'}>
            <StatLabel>Transfered To {shortAddress(data.to)}</StatLabel>
            <StatNumber>{`${amount} MATIC`}</StatNumber>
            <StatHelpText>
              {data && (
                <Link
                  href={`https://mumbai.polygonscan.com/tx/${data?.transactionHash}`}
                  target="_blank"
                >
                  View Transaction
                </Link>
              )}
            </StatHelpText>
          </Stat>
        ),
      })
    },
  })

  return (
    <VStack
      minH={{ base: '75vh', md: '70vh', lg: '82vh' }}
      wrap={'wrap'}
      align={'center'}
      justify={'center'}
    >
      <HStack
        minH={{ base: '75vh', md: '70vh', lg: '82vh' }}
        wrap={'wrap'}
        align={'center'}
        justify={'center'}
      >
        <Image src={TransferSvg} boxSize={{ base: '170px', md: 'xs' }}></Image>
        <Stack
          spacing={4}
          bg={useColorModeValue('white', 'gray.700')}
          rounded={'xl'}
          boxShadow={'lg'}
          p={{ base: '6', md: '12' }}
          my={{ base: '6', md: '12' }}
        >
          <Heading
            lineHeight={1.5}
            fontSize={{ base: '3xl', md: '4xl' }}
            color={'brand.500'}
            alignSelf={'center'}
          >
            Transfer Tokens 💸
          </Heading>
          <FormControl id="email">
            <FormLabel>Sender Address</FormLabel>
            <Heading>{shortAddress(address)}</Heading>
          </FormControl>
          <FormControl id="email">
            <FormLabel>Recipient Address</FormLabel>
            <Input
              placeholder="0xA0Cf…251e"
              _placeholder={{ color: 'gray.500' }}
              type="text"
              onChange={(e) => setTo(e.target.value)}
              value={to}
            />
          </FormControl>
          <FormControl id="">
            <FormLabel>Amount</FormLabel>
            <InputGroup>
              <Input
                placeholder="0.05"
                _placeholder={{ color: 'gray.500' }}
                type="text"
                onChange={(e) => setAmount(e.target.value)}
                value={amount}
              />
              <InputRightAddon children="MATIC" />
            </InputGroup>
          </FormControl>
          <Stack spacing={6}>
            <Button
              bg={'brand.500'}
              color={'white'}
              _disabled={{
                bg: 'gray.600',
                cursor: 'not-allowed',
              }}
              _hover={{
                bg: 'brand.600',
              }}
              onClick={(e) => {
                e.preventDefault()
                sendTransaction?.()
              }}
              isDisabled={isLoading || !sendTransaction || !to || !amount}
            >
              {isLoading && <Spinner size="sm" thickness="2px" color="white" mx={'3px'}/>}
              {isDisconnected ? 'Wallet Not Connected' : isLoading ? 'Sending...' : 'Send MATIC'}
            </Button>
          </Stack>
        </Stack>
      </HStack>
    </VStack>
  )
}
